/**
 * Wishlist Functionality
 * Uses localStorage as the primary source of truth and mirrors to cart attributes.
 */

class Wishlist {
  constructor() {
    this.storageKey = 'wishlist';
    this.cartAttributeKey = 'wishlist';
    this.wishlistItems = [];

    this.init();
  }

  async init() {
    if (!this.isLoggedIn()) {
      this.wishlistItems = [];
      this.initGuestMode();
      this.updateWishlistCount();
      return;
    }

    await this.loadWishlist();
    this.initButtons(document);
    this.updateWishlistCount();
    this.bindGlobalRefresh();
  }

  bindGlobalRefresh() {
    document.addEventListener('shopify:section:load', () => {
      this.initButtons(document);
    });

    document.addEventListener('wishlist:refresh-buttons', () => {
      this.initButtons(document);
    });
  }

  isLoggedIn() {
    return document.body.classList.contains('customer-logged-in')
      || window.location.pathname.includes('/account');
  }

  initGuestMode() {
    const wishlistButtons = document.querySelectorAll('[data-wishlist-btn]');

    wishlistButtons.forEach((btn) => {
      if (btn.dataset.wishlistBound === 'true') {
        return;
      }

      btn.dataset.wishlistBound = 'true';
      btn.setAttribute('aria-pressed', 'false');

      btn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.showLoginPrompt();
      });
    });
  }

  showLoginPrompt() {
    if (window.confirm('Please login to add items to your wishlist. Would you like to login now?')) {
      window.location.href = `/account/login?return_url=${encodeURIComponent(window.location.pathname + window.location.search)}`;
    }
  }

  async loadWishlist() {
    const localItems = this.readLocalWishlist();
    const cartItems = await this.readCartWishlist();

    this.wishlistItems = this.mergeWishlistItems(localItems, cartItems);
    this.writeLocalWishlist(this.wishlistItems);
  }

  readLocalWishlist() {
    try {
      return this.normalizeWishlistItems(JSON.parse(localStorage.getItem(this.storageKey) || '[]'));
    } catch (error) {
      console.error('Error reading wishlist from localStorage:', error);
      return [];
    }
  }

  writeLocalWishlist(items) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(items));
    } catch (error) {
      console.error('Error writing wishlist to localStorage:', error);
    }
  }

  async readCartWishlist() {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        return [];
      }

      const cart = await response.json();
      const rawWishlist = cart && cart.attributes ? cart.attributes[this.cartAttributeKey] : null;

      if (!rawWishlist) {
        return [];
      }

      return this.normalizeWishlistItems(JSON.parse(rawWishlist));
    } catch (error) {
      console.error('Error reading wishlist from cart attributes:', error);
      return [];
    }
  }

  normalizeWishlistItems(items) {
    if (!Array.isArray(items)) {
      return [];
    }

    const normalized = [];

    items.forEach((item) => {
      let normalizedItem = null;

      if (typeof item === 'string' || typeof item === 'number') {
        normalizedItem = {
          id: String(item),
        };
      } else if (item && typeof item === 'object') {
        normalizedItem = {};

        if (item.id !== undefined && item.id !== null && item.id !== '') {
          normalizedItem.id = String(item.id);
        }

        if (item.handle) {
          normalizedItem.handle = String(item.handle);
        }

        if (item.title) {
          normalizedItem.title = String(item.title);
        }

        if (item.url) {
          normalizedItem.url = String(item.url);
        }

        if (item.image) {
          normalizedItem.image = String(item.image);
        }

        if (item.addedAt) {
          normalizedItem.addedAt = String(item.addedAt);
        }
      }

      if (!normalizedItem || (!normalizedItem.id && !normalizedItem.handle)) {
        return;
      }

      normalized.push(normalizedItem);
    });

    return this.mergeWishlistItems(normalized);
  }

  mergeWishlistItems(...lists) {
    const itemMap = new Map();

    lists.flat().forEach((item) => {
      if (!item || (!item.id && !item.handle)) {
        return;
      }

      const itemKey = this.getItemKey(item);
      const existing = itemMap.get(itemKey) || {};

      itemMap.set(itemKey, {
        ...existing,
        ...item,
      });
    });

    return Array.from(itemMap.values());
  }

  getItemKey(item) {
    if (item.id) {
      return `id:${String(item.id)}`;
    }

    return `handle:${String(item.handle)}`;
  }

  async saveWishlist() {
    this.writeLocalWishlist(this.wishlistItems);

    if (!this.isLoggedIn()) {
      return false;
    }

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attributes: {
            [this.cartAttributeKey]: JSON.stringify(this.wishlistItems),
          },
        }),
      });

      return response.ok;
    } catch (error) {
      console.error('Error saving wishlist:', error);
      return false;
    }
  }

  initButtons(root = document) {
    const wishlistButtons = root.querySelectorAll('[data-wishlist-btn]');

    wishlistButtons.forEach((btn) => {
      this.syncButtonState(btn);

      if (btn.dataset.wishlistBound === 'true') {
        return;
      }

      btn.dataset.wishlistBound = 'true';

      btn.addEventListener('click', async (event) => {
        event.preventDefault();
        event.stopPropagation();
        await this.toggleWishlist(btn);
      });
    });
  }

  syncButtonState(btn) {
    const productId = btn.dataset.productId;
    const productHandle = btn.dataset.productHandle;
    const isActive = productId
      ? this.isInWishlist(productId)
      : this.isInWishlistByHandle(productHandle);

    btn.classList.toggle('active', isActive);
    btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');

    if (btn.dataset.addWishlistLabel || btn.dataset.removeWishlistLabel) {
      btn.setAttribute('aria-label', isActive
        ? (btn.dataset.removeWishlistLabel || 'Remove from wishlist')
        : (btn.dataset.addWishlistLabel || 'Add to wishlist'));
    }

    const textNode = btn.querySelector('[data-wishlist-button-text]');

    if (textNode && (btn.dataset.inactiveText || btn.dataset.activeText)) {
      textNode.textContent = isActive
        ? (btn.dataset.activeText || textNode.textContent)
        : (btn.dataset.inactiveText || textNode.textContent);
    }
  }

  isInWishlist(productId) {
    const normalizedId = String(productId);
    return this.wishlistItems.some((item) => String(item.id) === normalizedId);
  }

  isInWishlistByHandle(productHandle) {
    if (!productHandle) {
      return false;
    }

    const normalizedHandle = String(productHandle);
    return this.wishlistItems.some((item) => String(item.handle) === normalizedHandle);
  }

  buildWishlistItem(btn) {
    const productId = btn.dataset.productId ? String(btn.dataset.productId) : '';
    const productHandle = btn.dataset.productHandle ? String(btn.dataset.productHandle) : '';

    if (!productId && !productHandle) {
      return null;
    }

    return {
      id: productId || undefined,
      handle: productHandle || undefined,
      title: btn.dataset.productTitle ? String(btn.dataset.productTitle) : undefined,
      url: btn.dataset.productUrl ? String(btn.dataset.productUrl) : undefined,
      image: btn.dataset.productImage ? String(btn.dataset.productImage) : undefined,
      addedAt: new Date().toISOString(),
    };
  }

  async toggleWishlist(btn) {
    if (!this.isLoggedIn()) {
      this.showLoginPrompt();
      return false;
    }

    const item = this.buildWishlistItem(btn);

    if (!item) {
      return false;
    }

    const itemKey = this.getItemKey(item);
    const existingIndex = this.wishlistItems.findIndex((wishlistItem) => this.getItemKey(wishlistItem) === itemKey);

    if (existingIndex > -1) {
      this.wishlistItems.splice(existingIndex, 1);
      this.showNotification('Removed from wishlist');
    } else {
      this.wishlistItems.push(item);
      this.showNotification('Added to wishlist');
    }

    this.wishlistItems = this.mergeWishlistItems(this.wishlistItems);

    await this.saveWishlist();
    this.initButtons(document);
    this.updateWishlistCount();

    document.dispatchEvent(new CustomEvent('wishlist:updated', {
      detail: { items: this.getItems() },
    }));

    return true;
  }

  updateWishlistCount() {
    const countElements = document.querySelectorAll('[data-wishlist-count]');

    countElements.forEach((element) => {
      element.textContent = this.wishlistItems.length;
      element.style.display = this.wishlistItems.length > 0 ? 'block' : 'none';
    });
  }

  showNotification(message) {
    const toast = document.createElement('div');
    toast.className = 'wishlist-toast';
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      bottom: 30px;
      right: 30px;
      background: #333;
      color: white;
      padding: 15px 25px;
      border-radius: 8px;
      font-size: 14px;
      z-index: 10000;
      animation: slideInUp 0.3s ease;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    `;

    document.body.appendChild(toast);

    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideInUp {
        from {
          transform: translateY(100%);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }
    `;
    document.head.appendChild(style);

    setTimeout(() => {
      toast.style.animation = 'slideInUp 0.3s ease reverse';
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  getItems() {
    return this.wishlistItems.slice();
  }

  async clearWishlist() {
    this.wishlistItems = [];
    await this.saveWishlist();
    this.initButtons(document);
    this.updateWishlistCount();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.wishlistManager = new Wishlist();
  });
} else {
  window.wishlistManager = new Wishlist();
}
